import { ArrowLeftOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import { Alert, Button, Descriptions, Empty, Skeleton, Space, Tag, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createEntryPreviewToken, getSiteEntry, listSiteTaxonomies, listTaxonomyTerms, siteAssetVariantUrl } from '../../api'
import type { SaasApiError } from '../../api'
import type { ContentEntry, TaxonomyTerm } from '../../types'
import DeepZoomViewer from '../../components/DeepZoomViewer'

const { Title, Text } = Typography

const STATUS_LABEL: Record<string, string> = { draft: '草稿', published: '已发布', archived: '已归档' }
const STATUS_COLOR: Record<string, string> = { draft: 'default', published: 'green', archived: 'red' }
const VISIBILITY_LABEL: Record<string, string> = { public: '展示', unlisted: '隐藏（不索引）', private: '隐藏' }

interface GalleryImageField {
  asset_id?: string
  is_primary?: boolean
  caption?: string
}

/**
 * LiangCollection UI Fidelity (A1) — 条目详情（只读）。The list's 名称 column
 * now lands here rather than the edit page (spec §一/三十四): a read-only
 * admin view of everything `ItemEditorPage.tsx` lets an editor change, plus
 * — this round's actual headline feature — the real Deep Zoom /
 * OpenSeadragon high-resolution viewer for the cover image (spec §三).
 * 编辑 and 本地查看 stay one click away via the header actions, matching the
 * list row's own 铅笔/眼睛 icons exactly.
 */
export default function AdminItemDetailPage({
  workspaceId,
  siteId,
  workspaceSlug,
  siteSlug,
  siteBase,
}: {
  workspaceId: string
  siteId: string
  workspaceSlug?: string
  siteSlug?: string
  siteBase: string
}) {
  const { itemId = '' } = useParams()
  const navigate = useNavigate()
  const [entry, setEntry] = useState<ContentEntry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])
  const [topicTerms, setTopicTerms] = useState<TaxonomyTerm[]>([])
  const [previewOpening, setPreviewOpening] = useState(false)

  useEffect(() => {
    listSiteTaxonomies(workspaceId, siteId).then((r) => {
      const category = r.data.find((t) => t.key === 'category')
      const topic = r.data.find((t) => t.key === 'topic')
      if (category) listTaxonomyTerms(workspaceId, siteId, category.id).then((tr) => setCategoryTerms(tr.data))
      if (topic) listTaxonomyTerms(workspaceId, siteId, topic.id).then((tr) => setTopicTerms(tr.data))
    })
  }, [workspaceId, siteId])

  useEffect(() => {
    setLoading(true)
    setNotFound(false)
    setError(null)
    getSiteEntry(workspaceId, siteId, itemId)
      .then((r) => setEntry(r.data))
      .catch((err) => {
        const apiErr = err as SaasApiError
        if (apiErr.status === 404) setNotFound(true)
        else setError(apiErr.message)
      })
      .finally(() => setLoading(false))
  }, [workspaceId, siteId, itemId])

  if (loading) return <Skeleton active paragraph={{ rows: 10 }} style={{ marginTop: 16 }} />
  if (notFound) return <Empty description="该条目不存在或已被删除" style={{ marginTop: 80 }} />
  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (!entry) return null

  const f = entry.fields || {}
  const gallery = Array.isArray(f.gallery_images) ? (f.gallery_images as GalleryImageField[]) : []
  const coverAssetId =
    entry.cover_asset_id ?? gallery.find((g) => g.is_primary)?.asset_id ?? gallery[0]?.asset_id ?? null

  const termById = new Map<string, TaxonomyTerm>()
  for (const t of [...categoryTerms, ...topicTerms]) termById.set(t.id, t)
  const categoryTermIds = new Set(categoryTerms.map((t) => t.id))
  const topicTermIds = new Set(topicTerms.map((t) => t.id))
  const categories = entry.taxonomy_term_ids.filter((id) => categoryTermIds.has(id))
  const topics = entry.taxonomy_term_ids.filter((id) => topicTermIds.has(id))

  const localViewUrl = workspaceSlug && siteSlug ? `/site/${workspaceSlug}/${siteSlug}/items/${entry.slug}` : undefined

  // ITEM-DETAIL-PROGRESSIVE-IMAGE-P0: "本地查看" must work immediately
  // after creating/saving an entry, whether or not it's been published
  // yet or Deep Zoom has finished — a published entry needs no token
  // (public route already allows it), everything else gets one so the
  // owner can preview it right away instead of hitting "item not found".
  const openLocalView = async () => {
    if (!localViewUrl) return
    if (entry.status === 'published') {
      window.open(localViewUrl, '_blank')
      return
    }
    // Open the tab synchronously (within the click gesture) so popup
    // blockers never kick in, then redirect it once the token arrives —
    // an `await` before `window.open` would otherwise risk the browser
    // treating the call as no longer user-initiated.
    const win = window.open('about:blank', '_blank')
    setPreviewOpening(true)
    try {
      const { data } = await createEntryPreviewToken(workspaceId, siteId, entry.id)
      const url = `${localViewUrl}?preview_token=${encodeURIComponent(data.token)}`
      if (win) win.location.href = url
      else window.open(url, '_blank')
    } catch (err) {
      win?.close()
      message.error((err as SaasApiError).message || '生成预览链接失败')
    } finally {
      setPreviewOpening(false)
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <div>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(`${siteBase}/items`)} style={{ paddingLeft: 0 }}>
            返回条目列表
          </Button>
          <Title level={4} style={{ margin: '4px 0 0' }}>
            {entry.label}
            {f.title_original ? <Text type="secondary" style={{ fontSize: 14, marginLeft: 10 }}>{String(f.title_original)}</Text> : null}
          </Title>
          <Space size={8} style={{ marginTop: 4 }}>
            <Tag color={STATUS_COLOR[entry.status]}>{STATUS_LABEL[entry.status] ?? entry.status}</Tag>
            <Tag>{VISIBILITY_LABEL[entry.visibility] ?? entry.visibility}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>/{entry.slug}</Text>
          </Space>
        </div>
        <Space>
          <Button icon={<EyeOutlined />} onClick={openLocalView} disabled={!localViewUrl} loading={previewOpening}>
            本地查看
          </Button>
          <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`${siteBase}/items/${entry.id}/edit`)}>
            编辑
          </Button>
        </Space>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 560px', minWidth: 320 }}>
          {coverAssetId ? (
            <DeepZoomViewer
              workspaceId={workspaceId}
              assetId={coverAssetId}
              fallbackImageUrl={siteAssetVariantUrl(workspaceId, coverAssetId, 'original')}
              alt={entry.label}
              height={520}
            />
          ) : (
            <div style={{ height: 520, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Text type="secondary">暂无图片</Text>
            </div>
          )}
          {gallery.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {gallery.map((g) =>
                g.asset_id ? (
                  <img
                    key={g.asset_id}
                    src={siteAssetVariantUrl(workspaceId, g.asset_id, 'thumbnail')}
                    alt={g.caption ?? ''}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: g.asset_id === coverAssetId ? '2px solid #1677ff' : '1px solid #eee',
                    }}
                  />
                ) : null,
              )}
            </div>
          )}
        </div>

        <div style={{ flex: '1 1 420px', minWidth: 320 }}>
          <Descriptions column={1} bordered size="small" title="基本信息">
            <Descriptions.Item label="条目编号">{String(f.accession_number ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="中文名称">{String(f.title_zh ?? entry.label ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="英文/原文名称">{String(f.title_original ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="作者/制造者">{String(f.creator_display ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="年代">{String(f.creation_period_display ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="国家/地区">{String(f.region ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="材质概述">{String(f.material_summary ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="尺寸概述">{String(f.dimension_summary ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="条目分类">
              {categories.length ? categories.map((id) => <Tag key={id}>{termById.get(id)?.name ?? id}</Tag>) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="主题">
              {topics.length ? topics.map((id) => <Tag key={id} color="blue">{termById.get(id)?.name ?? id}</Tag>) : '—'}
            </Descriptions.Item>
          </Descriptions>

          {!!(f.short_summary || f.description || f.research_notes) && (
            <Descriptions column={1} bordered size="small" title="简介与研究" style={{ marginTop: 16 }}>
              {f.short_summary ? <Descriptions.Item label="一句话摘要">{String(f.short_summary)}</Descriptions.Item> : null}
              {f.description ? <Descriptions.Item label="条目简介">{String(f.description)}</Descriptions.Item> : null}
              {f.research_notes ? <Descriptions.Item label="研究备注">{String(f.research_notes)}</Descriptions.Item> : null}
            </Descriptions>
          )}

          <Descriptions column={1} bordered size="small" title="时间记录" style={{ marginTop: 16 }}>
            <Descriptions.Item label="创建时间">{new Date(entry.created_at).toLocaleString('zh-CN', { hour12: false })}</Descriptions.Item>
            <Descriptions.Item label="修改时间">{new Date(entry.updated_at).toLocaleString('zh-CN', { hour12: false })}</Descriptions.Item>
            {entry.published_at && (
              <Descriptions.Item label="发布时间">{new Date(entry.published_at).toLocaleString('zh-CN', { hour12: false })}</Descriptions.Item>
            )}
          </Descriptions>
        </div>
      </div>
    </div>
  )
}
